import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  SquaresFour as LayoutDashboard,
  Package,
  Users,
  ChartBar as BarChart3,
  ClipboardText as ClipboardList,
  SignOut as LogOut,
  List as MenuIcon,
  X,
  Storefront as Store,
  GearSix as Settings,
  CurrencyDollar as DollarSign,
  Calendar,
  Clock,
  Sun,
  Moon
} from '@phosphor-icons/react';

const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { slug } = useParams();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [darkMode, setDarkMode] = useState(() => {
    const saved = localStorage.getItem('pos-dark-mode');
    return saved ? JSON.parse(saved) : false;
  });

  // Get tenant slug for navigation
  const tenantSlug = slug || user?.tenantSlug || '';

  // Update time every second
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  // Persist dark mode
  useEffect(() => {
    localStorage.setItem('pos-dark-mode', JSON.stringify(darkMode));
  }, [darkMode]);

  const handleLogout = () => {
    logout();
    navigate(`/${tenantSlug}/login`);
  };

  const isActive = (path) => location.pathname === path;

  // Theme classes
  const bgClass = darkMode ? 'bg-primaryB-900' : 'bg-stone-50';
  const surfaceClass = darkMode ? 'bg-primaryB-800' : 'bg-primaryB-50';
  const textClass = darkMode ? 'text-white' : 'text-primaryB-900';
  const mutedClass = darkMode ? 'text-primaryB-400' : 'text-primaryB-500';
  const borderClass = darkMode ? 'border-white/[0.06]' : 'border-primaryB-200/60';

  // Define nav links based on role
  const getNavLinks = () => {
    const links = [
      { path: `/${tenantSlug}/dashboard`, label: 'Dashboard', icon: LayoutDashboard }
    ];

    // Owner/Admin gets full access
    if (user?.role === 'OWNER' || user?.role === 'ADMIN') {
      links.push(
        { path: `/${tenantSlug}/inventory`, label: 'Inventory', icon: Package },
        { path: `/${tenantSlug}/staff`, label: 'Staff', icon: Users },
        { path: `/${tenantSlug}/reports`, label: 'Reports', icon: BarChart3 },
        { path: `/${tenantSlug}/expenses`, label: 'Expenses', icon: DollarSign },
        { path: `/${tenantSlug}/audit`, label: 'Audit Log', icon: ClipboardList },
        { path: `/${tenantSlug}/settings`, label: 'Settings', icon: Settings }
      );
    }
    // Manager gets limited access
    else if (user?.role === 'MANAGER') {
      links.push(
        { path: `/${tenantSlug}/inventory`, label: 'Inventory', icon: Package },
        { path: `/${tenantSlug}/staff`, label: 'Staff', icon: Users },
        { path: `/${tenantSlug}/reports`, label: 'Reports', icon: BarChart3 },
        { path: `/${tenantSlug}/expenses`, label: 'Expenses', icon: DollarSign }
      );
    }
    // Cashier only sees POS
    // No additional links for cashier

    return links;
  };

  const navLinks = getNavLinks();

  return (
    <div className={`min-h-screen ${bgClass} flex flex-col`}>
      {/* Navbar */}
      <nav className={`${darkMode ? 'bg-primaryB-900/80' : 'bg-primaryB-50/80'} backdrop-blur-xl shadow-[0_1px_3px_rgba(0,0,0,0.05)] sticky top-0 z-40 safe-area-top`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Brand */}
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2">
                <div className="p-1.5 bg-blue-600 rounded-lg">
                  <Store weight="bold" className="w-5 h-5 text-white" />
                </div>
                <span className={`text-lg font-semibold ${textClass} hidden sm:inline`}>POS System</span>
              </div>
              <span className={`hidden md:inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${darkMode ? 'bg-blue-900/30 text-blue-400' : 'bg-blue-100 text-blue-800'}`}>
                {user?.tenantName}
              </span>
            </div>

            {/* Desktop Nav Links */}
            <div className="hidden lg:flex items-center gap-1">
              {navLinks.map((link) => {
                const Icon = link.icon;
                return (
                  <Link
                    key={link.path}
                    to={link.path}
                    className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                      isActive(link.path)
                        ? darkMode ? 'bg-blue-900/30 text-blue-400' : 'bg-blue-50 text-blue-700'
                        : `${mutedClass} ${darkMode ? 'hover:bg-primaryB-700 hover:text-white' : 'hover:bg-primaryB-100 hover:text-primaryB-900'}`
                    }`}
                  >
                    <Icon weight="bold" className="w-4 h-4" />
                    {link.label}
                  </Link>
                );
              })}
            </div>

            {/* User Menu */}
            <div className="hidden md:flex items-center gap-4">
              {/* Dark Mode Toggle */}
              <button
                onClick={() => setDarkMode(!darkMode)}
                className={`p-2 rounded-lg transition-colors ${darkMode ? 'text-primaryB-400 hover:text-white hover:bg-primaryB-700' : 'text-primaryB-500 hover:text-primaryB-900 hover:bg-primaryB-100'}`}
              >
                {darkMode ? <Sun weight="bold" className="w-4 h-4" /> : <Moon weight="bold" className="w-4 h-4" />}
              </button>
              {/* Date/Time Display */}
              <div className={`flex items-center gap-3 px-4 py-2 ${darkMode ? 'bg-primaryB-800' : 'bg-primaryB-100'} rounded-xl`}>
                <div className="flex items-center gap-1.5">
                  <Calendar weight="bold" className={`w-3.5 h-3.5 ${mutedClass}`} />
                  <span className={`text-[10px] font-bold uppercase ${darkMode ? 'text-primaryB-300' : 'text-primaryB-700'}`}>
                    {currentTime.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                  </span>
                </div>
                <div className={`w-px h-4 ${darkMode ? 'bg-primaryB-600' : 'bg-primaryB-300'}`}></div>
                <div className="flex items-center gap-1.5">
                  <Clock weight="bold" className={`w-3.5 h-3.5 ${mutedClass}`} />
                  <span className={`text-[10px] font-bold uppercase ${darkMode ? 'text-primaryB-300' : 'text-primaryB-700'}`}>
                    {currentTime.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                  </span>
                </div>
              </div>
              <div className="text-right">
                <p className={`text-sm font-medium ${textClass}`}>{user?.fullName}</p>
                <p className={`text-xs ${mutedClass}`}>{user?.role}</p>
              </div>
              <button
                onClick={handleLogout}
                className={`inline-flex items-center gap-2 px-3 py-2 text-sm font-medium ${mutedClass} ${darkMode ? 'hover:text-white hover:bg-primaryB-700' : 'hover:text-primaryB-900 hover:bg-primaryB-100'} rounded-lg transition-colors`}
              >
                <LogOut weight="bold" className="w-4 h-4" />
              </button>
            </div>

            {/* Mobile menu button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className={`lg:hidden p-2 ${mutedClass} ${darkMode ? 'hover:text-white hover:bg-primaryB-700' : 'hover:text-primaryB-900 hover:bg-primaryB-100'} rounded-lg transition-colors`}
            >
              {mobileMenuOpen ? <X weight="bold" className="w-6 h-6" /> : <MenuIcon weight="bold" className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className={`lg:hidden border-t ${borderClass} ${surfaceClass} absolute w-full shadow-elevated`}>
            <div className="px-4 py-3 space-y-1">
              {/* Tenant Name on Mobile */}
              <div className={`px-3 py-2 text-sm font-medium text-blue-600 border-b ${borderClass} mb-2`}>
                {user?.tenantName}
              </div>

              {navLinks.map((link) => {
                const Icon = link.icon;
                return (
                  <Link
                    key={link.path}
                    to={link.path}
                    onClick={() => setMobileMenuOpen(false)}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                      isActive(link.path)
                        ? darkMode ? 'bg-blue-900/30 text-blue-400' : 'bg-blue-50 text-blue-700'
                        : `${mutedClass} ${darkMode ? 'hover:bg-primaryB-700 hover:text-white' : 'hover:bg-primaryB-100 hover:text-primaryB-900'}`
                    }`}
                  >
                    <Icon weight="bold" className="w-5 h-5" />
                    {link.label}
                  </Link>
                );
              })}

              {/* Dark Mode Toggle - Mobile */}
              <button
                onClick={() => setDarkMode(!darkMode)}
                className={`flex items-center gap-3 w-full px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${mutedClass} ${darkMode ? 'hover:bg-primaryB-700 hover:text-white' : 'hover:bg-primaryB-100 hover:text-primaryB-900'}`}
              >
                {darkMode ? <Sun weight="bold" className="w-5 h-5" /> : <Moon weight="bold" className="w-5 h-5" />}
                {darkMode ? 'Light Mode' : 'Dark Mode'}
              </button>

              <div className={`pt-3 mt-3 border-t ${borderClass}`}>
                <div className="px-3 py-2">
                  <p className={`text-sm font-medium ${textClass}`}>{user?.fullName}</p>
                  <p className={`text-xs ${mutedClass}`}>{user?.role}</p>
                </div>
                <button
                  onClick={handleLogout}
                  className="flex items-center gap-3 w-full px-3 py-2.5 text-sm font-medium text-negative-600 hover:bg-negative-50 rounded-lg transition-colors"
                >
                  <LogOut weight="bold" className="w-5 h-5" />
                  Logout
                </button>
              </div>
            </div>
          </div>
        )}
      </nav>

      {/* Main Content - Scrollable */}
      <main id="main-content" className="flex-1 overflow-auto page-gradient-warm">
        {React.cloneElement(children, {
          darkMode,
          surfaceClass,
          textClass,
          mutedClass,
          borderClass,
          bgClass
        })}
      </main>
    </div>
  );
};

export default Layout;
